import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';

const styles = () => ({
  root: {
    minWidth: 143,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'nowrap',
    '@media (max-width: 960px)': {
      width: '100%',
    },
  },

  filter: {
    width: 'auto',
    minWidth: 90,
    height: 40,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    padding: '6px 12px 6px 14px',
    background: '#FFFFFF',
    border: '1px solid #C0D2E5',
    borderRadius: 0,
    position: 'relative',
    cursor: 'pointer',
    '&:focus-visible': { outline: '2px solid rgba(12,112,232,.6)', outlineOffset: 2 },
  },

  valueText: {
    flex: 1,
    minWidth: 0,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    color: '#181D27',
    fontFamily: 'Roboto, var(--font-sans)',
    fontSize: 14,
    lineHeight: '20px',
    fontWeight: 400,
  },

  sortIcon: {
    flex: '0 0 auto',
    display: 'inline-flex',
    lineHeight: 0,
    color: '#0C70E8',
  },

  /* dropdown */
  menu: {
    position: 'absolute',
    top: 'calc(100% + 6px)',
    left: -1,
    width: 'calc(100% + 2px)',
    background: '#fff',
    border: '1px solid #C0D2E5',
    boxShadow: '16px 16px 32px rgba(23,65,151,0.12)',
    padding: 0,
    display: 'none',
    zIndex: 20,
  },
  menuOpen: { display: 'block' },

  option: {
    padding: '12px 14px',
    fontFamily: 'var(--font-sans)',
    fontSize: 16,
    lineHeight: '24px',
    color: '#181D27',
    userSelect: 'none',
  },
  optionHover: { background: '#E9F3FF' },
  optionSelected: {
    background: '#E3F2FF',
    color: '#0C70E8',
    fontWeight: 500,
  },
});

function PbFilter(props) {
  const { classes, filters, onSelected, orderByAsc } = props;

  const [optionSelected, setOptionSelected] = useState('Duration');
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const boxRef = useRef(null);

  /* Direction is fixed per option (design review 2026-10-14 — no asc/desc
     toggle in the UI). Asc unless the option overrides it. */
  const directionFor = (value) => {
    const f = (filters ?? []).find((x) => x.Value === value);
    if (f && typeof f.Ascending === 'boolean') return f.Ascending;
    return typeof orderByAsc === 'boolean' ? orderByAsc : true;
  };

  /* Notifica cambios al padre */
  useEffect(() => {
    if (onSelected) onSelected(optionSelected, directionFor(optionSelected));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [optionSelected]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (!boxRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const choose = (val) => {
    setOptionSelected(val);
    setOpen(false);
  };

  const list = filters ?? [];
  const selectedIdx = Math.max(0, list.findIndex((f) => f.Value === optionSelected));
  const openMenu = () => {
    setActiveIdx(selectedIdx);
    setOpen(true);
  };

  // Keyboard model of a listbox: arrows move the highlighted option, Enter/Space
  // pick it, Esc/Tab close. Focus stays on the trigger (aria-activedescendant).
  const onKey = (e) => {
    if (e.key === 'Escape' || e.key === 'Tab') {
      setOpen(false);
      return;
    }
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (!open) return openMenu();
      const step = e.key === 'ArrowDown' ? 1 : -1;
      setActiveIdx((i) => (i + step + list.length) % list.length);
      return;
    }
    if ((e.key === 'Home' || e.key === 'End') && open) {
      e.preventDefault();
      setActiveIdx(e.key === 'Home' ? 0 : list.length - 1);
      return;
    }
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (open && list[activeIdx]) choose(list[activeIdx].Value);
      else openMenu();
    }
  };

  const current =
    (filters ?? []).find((f) => f.Value === optionSelected)?.Description ?? optionSelected;

  return (
    <div className={`${classes.root} filter-select`}>
      <span
        style={{
          color: '#002857',
          fontFamily: 'Roboto, var(--font-sans)',
          fontSize: 14,
          lineHeight: '20px',
          letterSpacing: '0',
          fontWeight: 400,
          whiteSpace: 'nowrap',
          flex: '0 0 auto',
        }}>
        Sort by
      </span>

      <div
        ref={boxRef}
        className={classes.filter}
        role='combobox'
        tabIndex={0}
        onClick={() => (open ? setOpen(false) : openMenu())}
        onKeyDown={onKey}
        aria-label='Sort by'
        aria-haspopup='listbox'
        aria-expanded={open}
        aria-controls='sort-listbox'
        aria-activedescendant={open ? `sort-option-${activeIdx}` : undefined}>
        <span className={classes.valueText}>{current}</span>

        {/* sort_24dp icon —三条 bars, direction-neutral (no asc/desc toggle) */}
        <span className={classes.sortIcon} aria-hidden='true'>
          <svg width='20' height='20' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
            <path d='M3 18v-2h6v2H3zm0-5v-2h12v2H3zm0-5V6h18v2H3z' fill='currentColor' />
          </svg>
        </span>

        <div id='sort-listbox' className={`${classes.menu} ${open ? classes.menuOpen : ''}`} role='listbox'>
          {list.map((val, i) => (
            <div
              key={val.Value}
              id={`sort-option-${i}`}
              role='option'
              aria-selected={val.Value === optionSelected}
              className={[
                classes.option,
                val.Value === optionSelected ? classes.optionSelected : '',
                open && i === activeIdx ? classes.optionHover : '',
              ].join(' ')}
              onMouseEnter={() => setActiveIdx(i)}
              onClick={(e) => {
                e.stopPropagation();
                choose(val.Value);
              }}>
              {val.Description}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

PbFilter.propTypes = {
  filters: PropTypes.array,
  onSelected: PropTypes.func,
  orderByAsc: PropTypes.bool,
};
PbFilter.defaultProps = {
  filters: [
    { Description: 'Duration', Value: 'Duration', Ascending: true },
    { Description: 'Most Recent', Value: 'UploadTime', Ascending: false },
    { Description: 'Title', Value: 'Title', Ascending: true },
  ],
  orderByAsc: true,
};

export default withStyles(styles)(PbFilter);
